import { Injectable, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { ApiService } from '../../../../core/services/api.service';
import {
	ErrorCatalogLocale,
	IErrorsNumberCopy,
	IErrorsNumberCopyKeys,
	IErrorsNumberDeleteResponse,
	IErrorsNumberDetailParams,
	IErrorsNumberFormPayload,
	IErrorsNumberResponse,
	IErrorsNumbersItem,
	IErrorsNumbersListPayload,
	IErrorsNumbersListResponse,
	IErrorsNumberUpdatePayload,
} from '../models/error-catalog.models';

/**
 * Catálogo unificado de errores (NIF-748).
 * CRUD HTTP al estilo ClientAlerts / LegalTerms + cache de resolución para labels de proceso.
 */
@Injectable({
	providedIn: 'root',
})
export class ErrorsNumbersService extends ApiService {
	private static readonly BASE = `${environment.apiUrl}/errors-numbers`;
	private static readonly ITEM = `${environment.apiUrl}/errors-number`;

	/** Catálogo en memoria (código → ítem) para labels de proceso. */
	private readonly catalogByNumber = signal<Map<number, IErrorsNumbersItem>>(new Map());
	private catalogLoad$: Observable<IErrorsNumbersItem[]> | null = null;

	public list(body: IErrorsNumbersListPayload = {}): Observable<IErrorsNumbersListResponse> {
		const payload: IErrorsNumbersListPayload = {
			per_page: 25,
			...body,
		};

		return this.http.post<IErrorsNumbersListResponse>(ErrorsNumbersService.BASE, payload, {
			headers: this.getHeaders(),
		});
	}

	/** Compat workflows: listado completo sin filtros. */
	public listAll(perPage: number = 1000, type?: string): Observable<IErrorsNumbersListResponse> {
		return this.list({ per_page: perPage, type });
	}

	public detail(uuid: string, params: IErrorsNumberDetailParams = {}): Observable<IErrorsNumbersItem> {
		return this.http
			.get<IErrorsNumberResponse>(`${ErrorsNumbersService.ITEM}/${uuid}`, {
				headers: this.getHeaders(),
				params: params.lang ? { lang: params.lang } : undefined,
			})
			.pipe(map((response) => response.data));
	}

	public create(body: IErrorsNumberFormPayload): Observable<IErrorsNumbersItem> {
		return this.http
			.post<IErrorsNumberResponse>(ErrorsNumbersService.ITEM, body, {
				headers: this.getHeaders(),
			})
			.pipe(
				map((response) => response.data),
				tap(() => this.invalidateCatalogCache()),
			);
	}

	public update(uuid: string, body: IErrorsNumberUpdatePayload): Observable<IErrorsNumbersItem> {
		return this.http
			.patch<IErrorsNumberResponse>(`${ErrorsNumbersService.ITEM}/${uuid}`, body, {
				headers: this.getHeaders(),
			})
			.pipe(
				map((response) => response.data),
				tap(() => this.invalidateCatalogCache()),
			);
	}

	public delete(uuid: string): Observable<IErrorsNumberDeleteResponse> {
		return this.http
			.delete<IErrorsNumberDeleteResponse>(`${ErrorsNumbersService.ITEM}/${uuid}`, {
				headers: this.getHeaders(),
			})
			.pipe(tap(() => this.invalidateCatalogCache()));
	}

	/** Carga el catálogo una vez y lo cachea para resolución de títulos. */
	public ensureCatalogLoaded(): Observable<IErrorsNumbersItem[]> {
		if (this.catalogByNumber().size > 0) {
			return of(Array.from(this.catalogByNumber().values()));
		}
		if (!this.catalogLoad$) {
			this.catalogLoad$ = this.list({ per_page: 2000 }).pipe(
				map((res) => res?.data ?? []),
				tap((items) => {
					const byNumber = new Map<number, IErrorsNumbersItem>();
					for (const item of items) {
						byNumber.set(item.number, item);
					}
					this.catalogByNumber.set(byNumber);
				}),
				catchError(() => {
					this.catalogLoad$ = null;
					return of([] as IErrorsNumbersItem[]);
				}),
				shareReplay(1),
			);
		}
		return this.catalogLoad$;
	}

	public invalidateCatalogCache(): void {
		this.catalogByNumber.set(new Map());
		this.catalogLoad$ = null;
	}

	public resolveTitle(code: number, locale: string): string | null {
		return this.resolveField(code, locale, 'title');
	}

	public resolveMessage(code: number, locale: string): string | null {
		return this.resolveField(code, locale, 'message');
	}

	/**
	 * Resolución universal de copy KO: catálogo BBDD → fallback i18n.
	 * Si se pasan claves distintas de TITLE_/SUBTITLE_{code} (p.ej. 2014 MATCH_DATA), se usa solo i18n.
	 */
	public resolveCopy(
		code: number,
		locale: string,
		instant: (key: string) => string,
		keys?: IErrorsNumberCopyKeys,
	): IErrorsNumberCopy {
		const titleKey = keys?.titleKey ?? `ERRORS.PROCESS.TITLE_${code}`;
		const subtitleKey = keys?.subtitleKey ?? `ERRORS.PROCESS.SUBTITLE_${code}`;
		const useCatalogTitle = titleKey === `ERRORS.PROCESS.TITLE_${code}`;
		const useCatalogSubtitle = subtitleKey === `ERRORS.PROCESS.SUBTITLE_${code}`;

		return {
			title: (useCatalogTitle ? this.resolveTitle(code, locale) : null) ?? instant(titleKey),
			subtitle: (useCatalogSubtitle ? this.resolveMessage(code, locale) : null) ?? instant(subtitleKey),
		};
	}

	private resolveField(code: number, locale: string, field: 'title' | 'message'): string | null {
		const item = this.catalogByNumber().get(code);
		if (!item) {
			return null;
		}
		const loc = locale.toLowerCase() as ErrorCatalogLocale;
		const tr = item.translations?.[loc] ?? item.translations?.['en'];
		if (field === 'title') {
			return tr?.title ?? item.error ?? null;
		}
		return tr?.message ?? tr?.title ?? item.error ?? null;
	}
}
